import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface SelectionFooterButton {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: ButtonVariant;
  colorVariant?: ButtonColorVariant;
  action: () => Promise<void> | void;
}

export interface SelectionFooterProps {
  selection: string[];
  totalCount: number;
  showSelectionCount?: boolean;
  onClearSelection: () => void;
  onSelectAll?: () => void;
  additionalButtons?: SelectionFooterButton[];
}

export function SelectionFooter({
  selection,
  totalCount,
  showSelectionCount = true,
  onClearSelection,
  onSelectAll,
  additionalButtons = [],
}: SelectionFooterProps) {
  const { t } = useTranslation("common");
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  if (selection.length === 0) return null;

  const handleButtonClick = async (button: SelectionFooterButton, index: number) => {
    const key = `${index}-${button.label}`;
    setLoadingStates((prev) => ({ ...prev, [key]: true }));

    try {
      await button.action();
    } finally {
      setLoadingStates((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4",
        showSelectionCount ? "sm:justify-between" : "sm:justify-end",
      )}
    >
      {showSelectionCount && (
        <span className="text-center text-sm font-medium sm:text-left">
          {t("SelectedOfTotal", { count: selection.length, total: totalCount })}
        </span>
      )}
      <div className="flex flex-wrap justify-center gap-2 sm:justify-end">
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Sm}
          onClick={onClearSelection}
          className="text-xs sm:text-sm"
        >
          {t("common:ClearSelection")}
        </Button>
        {onSelectAll && selection.length < totalCount && (
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            size={ButtonSize.Sm}
            onClick={onSelectAll}
            className="text-xs sm:text-sm"
          >
            {t("SelectAll")}
          </Button>
        )}
        {additionalButtons.map((button, index) => {
          const key = `${index}-${button.label}`;
          const isLoading = loadingStates[key] || false;
          const IconComponent = button.icon;

          return (
            <Button
              key={key}
              variant={button.variant ?? ButtonVariant.Filled}
              colorVariant={button.colorVariant}
              size={ButtonSize.Sm}
              disabled={isLoading}
              className={cn("flex items-center gap-1.5 text-xs sm:text-sm")}
              onClick={() => handleButtonClick(button, index)}
            >
              {!isLoading && IconComponent && <IconComponent className={cn("size-3 sm:size-4")} />}
              {isLoading && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />}
              <span className="hidden sm:inline">{button.label}</span>
              <span className="sm:hidden">
                {button.label.slice(0, 8)}
                {button.label.length > 8 ? "..." : ""}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
